import { useEffect, useState } from 'react'

import { CopyOutlined, SlidersOutlined } from '@ant-design/icons'

import { Alert, Button, Card, Form, InputNumber, Space, Typography, message } from 'antd'

import { adminApi } from '../../api'

import { PageHeader } from '../../components/ui'

import './QualitySettings.css'

type DedupCfg = {
  fingerprint_max: number
  sample_head_pct: number
  sample_middle_pct: number
  sample_tail_pct: number
  min_match_chars: number
}

export default function DedupSettings() {
  const [dedupForm] = Form.useForm<DedupCfg>()
  const [dedupLoading, setDedupLoading] = useState(false)

  useEffect(() => {
    adminApi.get<DedupCfg>('/admin/settings/dedup').then((r) => dedupForm.setFieldsValue(r.data))
  }, [dedupForm])

  const saveDedup = () => {
    void dedupForm.validateFields().then((values) => {
      setDedupLoading(true)
      adminApi
        .put('/admin/settings/dedup', values)
        .then((r) => {
          message.success('查重配置已保存')
          if (r.data) dedupForm.setFieldsValue(r.data as DedupCfg)
        })
        .catch((e) => message.error(e.message))
        .finally(() => setDedupLoading(false))
    })
  }

  return (
    <div className="quality-settings">
      <PageHeader
        title="查重设置"
        description="控制前台「文件查重」功能的灵敏度。与「文档质量评估」无关，一般保持默认即可。"
      />

      <Alert
        type="info"
        showIcon
        style={{ marginBottom: 16 }}
        message="这个功能做什么？"
        description={
          <ul className="quality-settings-intro">
            <li>
              <strong>你会看到什么</strong>：用户在「文件查重」页上传文档后，系统找出知识库中内容相似的已有文档。
            </li>
            <li>
              <strong>和「文档质量评估」的区别</strong>：质量评估是入库时自动打标签；查重是用户主动发起的一次比对操作。
            </li>
            <li>
              <strong>改完何时生效</strong>：修改阈值后，需对历史文档执行「查重回填」才会按新规则重新比对。
            </li>
          </ul>
        }
      />

      <Form form={dedupForm} layout="vertical" className="quality-settings-form">
        <Card
          className="quality-card quality-card--full"
          title={
            <Space>
              <CopyOutlined />
              候选文档召回（高级）
            </Space>
          }
        >
          <Typography.Text type="secondary" className="quality-card-desc">
            长文档不会逐字比对全文，而是从开头、中间、结尾各取一段做快速匹配。下列参数控制取样范围，一般无需修改。
          </Typography.Text>
          <div className="quality-field-grid quality-field-grid--compact">
            <Form.Item
              name="fingerprint_max"
              label="取样片段总数"
              rules={[{ required: true, message: '请填写取样片段总数' }]}
              extra="默认 400，范围 50～2000"
            >
              <InputNumber min={50} max={2000} step={50} style={{ width: '100%' }} />
            </Form.Item>
            <Form.Item
              name="sample_head_pct"
              label="开头占比 %"
              rules={[{ required: true, message: '请填写开头占比' }]}
              extra="从文档开头取样的比例"
            >
              <InputNumber min={0} max={100} step={5} style={{ width: '100%' }} />
            </Form.Item>
            <Form.Item
              name="sample_middle_pct"
              label="中间占比 %"
              rules={[{ required: true, message: '请填写中间占比' }]}
              extra="从文档中间取样的比例"
            >
              <InputNumber min={0} max={100} step={5} style={{ width: '100%' }} />
            </Form.Item>
            <Form.Item
              name="sample_tail_pct"
              label="结尾占比 %"
              rules={[
                { required: true, message: '请填写结尾占比' },
                {
                  validator: async () => {
                    const h = Number(dedupForm.getFieldValue('sample_head_pct') || 0)
                    const m = Number(dedupForm.getFieldValue('sample_middle_pct') || 0)
                    const t = Number(dedupForm.getFieldValue('sample_tail_pct') || 0)
                    if (h + m + t <= 0) {
                      return Promise.reject(new Error('开头/中间/结尾占比之和须大于 0'))
                    }
                  },
                },
              ]}
              extra="从文档结尾取样的比例；保存时自动归一化为 100%"
            >
              <InputNumber min={0} max={100} step={5} style={{ width: '100%' }} />
            </Form.Item>
          </div>
        </Card>

        <Card
          className="quality-card quality-card--full"
          title={
            <Space>
              <SlidersOutlined />
              相似度判定
            </Space>
          }
        >
          <Typography.Text type="secondary" className="quality-card-desc">
            两段文字连续相同多少字才算「相似」。数值越小越敏感（更容易判为重复），越大越严格。
          </Typography.Text>
          <div className="quality-field-grid quality-field-grid--compact">
            <Form.Item
              name="min_match_chars"
              label="最短相同字数"
              rules={[{ required: true, message: '请填写最短相同字数' }]}
              extra="默认 13 字，范围 6～100"
            >
              <InputNumber min={6} max={100} step={1} style={{ width: '100%' }} />
            </Form.Item>
          </div>
        </Card>

        <div className="quality-settings-footer">
          <Button type="primary" loading={dedupLoading} onClick={saveDedup}>
            保存配置
          </Button>
        </div>
      </Form>
    </div>
  )
}
